import {
  forwardRef,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import './CodeInput.css';

export interface CodeInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
> {
  value: string;
  onChange: (code: string) => void;
  /** How many digits the code has; 6 for an authenticator app. */
  length?: number;
  /** Called once, with the code, the moment the last digit lands (typed, pasted or filled in). */
  onComplete?: ((code: string) => void) | undefined;
}

/**
 * A one-time code: one real input (so autofill from a message, paste and screen readers all see
 * a single field) drawn as one cell per digit. Digits only; the cells follow the value and the
 * caret sits on the next empty one while the field has focus.
 */
export const CodeInput = forwardRef<HTMLInputElement, CodeInputProps>(function CodeInput(
  { value, onChange, length = 6, onComplete, className, onFocus, onBlur, disabled, ...rest },
  ref,
) {
  const [focused, setFocused] = useState(false);
  const change = (e: ChangeEvent<HTMLInputElement>): void => {
    const next = e.currentTarget.value.replace(/\D/g, '').slice(0, length);
    onChange(next);
    if (next.length === length && next !== value) onComplete?.(next);
  };
  const classes = ['pui-code', disabled && 'pui-code--disabled', className].filter(Boolean);
  return (
    <div className={classes.join(' ')} data-focused={focused ? '' : undefined}>
      <Cells value={value} length={length} focused={focused} />
      <input
        ref={ref}
        className="pui-code__input"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={length}
        spellCheck={false}
        autoCapitalize="off"
        value={value}
        disabled={disabled}
        onChange={change}
        onFocus={(e) => (setFocused(true), onFocus?.(e))}
        onBlur={(e) => (setFocused(false), onBlur?.(e))}
        {...rest}
      />
    </div>
  );
});

/** The digits as drawn: one cell each, the caret on the next empty one while focused. */
function Cells({
  value,
  length,
  focused,
}: {
  value: string;
  length: number;
  focused: boolean;
}): ReactNode {
  const caret = Math.min(value.length, length - 1);
  return (
    <div className="pui-code__cells" aria-hidden="true">
      {Array.from({ length }, (_, i) => (
        <span
          key={i}
          className="pui-code__cell"
          data-filled={value[i] ? '' : undefined}
          data-caret={focused && i === caret ? '' : undefined}
        >
          {value[i] ?? ''}
        </span>
      ))}
    </div>
  );
}
